// tileWorker.ts - the terrain decode, off the main thread.
//
// One file, two halves. The bottom half runs INSIDE the worker and does the
// arithmetic; the top half is the typed client the main thread holds. The
// arithmetic itself is not here at all - it is the same exported functions in
// plotField.ts that the synchronous path calls - so a number cannot depend on
// which thread produced it, which is the property tools/preview/field-agreement.mjs
// exists to hold.
//
// What moves: the two Float32Array builds over a plot's u16 field (33 MB and
// 4.2 M iterations for a 2048 tile) and the box-filtered mip chain over the
// result. They come back as transferable buffers, so the main thread is left
// with a texture upload and no copy.
//
// `decodeTile` is the same arrangement one layer up: the dequantiser and the
// WebP decode are tileSource.ts's exported `decodeTilePayloads`, which the main
// thread also calls when there is no worker, so a tile's metres do not depend
// on where it decoded either.
//
// `reliefHeights` is a tile's procedural relief, tileRelief.ts's
// `tileReliefHeights`, which the main thread's sampler also answers from.
//
// `createTileWorkerPool` is the same client behind more than one thread: the
// Atlas creates one, `createTileWorker` stays for a caller that wants exactly
// one (the field-agreement check compares against a single worker on
// purpose).
//
// Design: docs/roadmap/07-performance-and-instruments.md, "Workers".
// The tile contract decodeTile answers to: docs/ATLAS-TERRAIN.md,
// "The runtime" (TileDecode).

import { buildLatticeBuffers, type LatticeBuffers, type LatticePlace } from './plotLattice.ts';
import { buildFieldBuffers, type FieldBuffers, type HeightMip } from './plotField.ts';
import { decodeTilePayloads, type DecodedAlbedo, type DecodedTilePayloads } from './tileSource.ts';
import {
  tileReliefHeights, tileReliefLattice, type TileReliefMaps, type TileReliefSwitches,
} from './tileRelief.ts';
import type { TileBounds, TileClaims, TileKey } from './tileTree';

// -----------------------------------------------------------------------------
// The message union
// -----------------------------------------------------------------------------

/** A plot or region's u16 height field, straight off the wire. */
export interface DecodeFieldRequest {
  readonly kind: 'decodeField';
  readonly id: number;
  /** Little-endian u16, row-major. Transferred in, so the sender loses it. */
  readonly raw: ArrayBuffer;
  readonly width: number;
  readonly height: number;
  readonly minM: number;
  readonly reliefM: number;
}

/**
 * A quadtree tile's payloads, to become the `LoadedTile` buffers.
 *
 * Both payloads are TRANSFERRED in, so the sender loses them, and both are
 * separately nullable because height and albedo stop at different levels.
 */
export interface DecodeTileRequest {
  readonly kind: 'decodeTile';
  readonly id: number;
  readonly key: TileKey;
  readonly radiusM: number;
  readonly height: ArrayBuffer | null;
  readonly albedo: ArrayBuffer | null;
  /** The artificial fill's bytes, null whenever its layer was off at the fetch. */
  readonly artificial: ArrayBuffer | null;
  /** Which resolution to bake the claim texture at, and the aggregated
   *  claimed set at it; null skips the claim texture entirely. */
  readonly claimRes: number | null;
  readonly claimCells: readonly string[] | null;
}

/**
 * The plot mesh's surface lattice, as vertex and index buffers.
 *
 * Shaped like `decodeField`: the arithmetic is `plotLattice.ts`'s exported
 * function, which the synchronous fallback in `DtmPlot` calls too, and the
 * result comes back as transferables the main thread wraps with no copy.
 */
export interface BuildLatticeRequest {
  readonly kind: 'buildLattice';
  readonly id: number;
  readonly radiusM: number;
  readonly rings: number;
  readonly places: readonly LatticePlace[];
  readonly origin: { readonly x: number; readonly z: number };
}

/**
 * A tile's procedural relief heights over `tileReliefLattice`, the same
 * `reliefHeightAt` the main thread's sampler runs. The class maps are 16 MB,
 * so each worker is sent them once per `mapsKey` and keeps them.
 */
export interface ReliefHeightsRequest {
  readonly kind: 'reliefHeights';
  readonly id: number;
  readonly mapsKey: number;
  /** Null when this worker already holds `mapsKey`'s maps. */
  readonly maps: TileReliefMaps | null;
  readonly switches: TileReliefSwitches;
  readonly bounds: TileBounds;
  readonly radiusM: number;
  readonly quads: number;
}

export type TileWorkerRequest = DecodeFieldRequest | DecodeTileRequest | BuildLatticeRequest | ReliefHeightsRequest;

export interface DecodeFieldResponse {
  readonly kind: 'decodeField';
  readonly id: number;
  readonly norm: Float32Array<ArrayBuffer>;
  readonly height: Float32Array<ArrayBuffer>;
  readonly mips: readonly HeightMip[];
}

export interface DecodeTileResponse {
  readonly kind: 'decodeTile';
  readonly id: number;
  readonly height: Float32Array<ArrayBuffer> | null;
  readonly albedo: DecodedAlbedo | null;
  /** The albedo bytes handed back: this thread has no `createImageBitmap`. */
  readonly albedoRaw: ArrayBuffer | null;
  readonly artificial: DecodedAlbedo | null;
  readonly artificialRaw: ArrayBuffer | null;
  readonly bounds: TileBounds;
  readonly claims: TileClaims | null;
}

export interface BuildLatticeResponse {
  readonly kind: 'buildLattice';
  readonly id: number;
  readonly position: Float32Array<ArrayBuffer>;
  readonly index: Uint32Array<ArrayBuffer>;
}

export interface ReliefHeightsResponse {
  readonly kind: 'reliefHeights';
  readonly id: number;
  readonly heights: Float64Array<ArrayBuffer>;
  /** The evaluation's own time on the worker. */
  readonly ms: number;
}

export interface WorkerErrorResponse {
  readonly kind: 'error';
  readonly id: number;
  readonly message: string;
}

export type TileWorkerResponse =
  DecodeFieldResponse | DecodeTileResponse | BuildLatticeResponse | ReliefHeightsResponse | WorkerErrorResponse;

/** What a caller supplies; the id and the kind are the client's business. */
export type DecodeFieldInput = Omit<DecodeFieldRequest, 'kind' | 'id'>;
export type DecodeTileInput = Omit<DecodeTileRequest, 'kind' | 'id'>;
export type BuildLatticeInput = Omit<BuildLatticeRequest, 'kind' | 'id'>;
export type ReliefHeightsInput = Omit<ReliefHeightsRequest, 'kind' | 'id' | 'mapsKey' | 'maps'> & {
  readonly maps: TileReliefMaps;
};
export interface ReliefHeights {
  readonly heights: Float64Array<ArrayBuffer>;
  readonly ms: number;
}

const reliefMapsKeys = new WeakMap<TileReliefMaps, number>();
let nextReliefMapsKey = 1;

/** The request for one worker, carrying the maps only if `sent` says it has not had them. */
function reliefRequest(id: number, input: ReliefHeightsInput, sent: Set<number>): ReliefHeightsRequest {
  let mapsKey = reliefMapsKeys.get(input.maps);
  if (mapsKey === undefined) {
    mapsKey = nextReliefMapsKey++;
    reliefMapsKeys.set(input.maps, mapsKey);
  }
  const first = !sent.has(mapsKey);
  sent.add(mapsKey);
  const { reliefInert, reliefUnconditioned, reliefRepeat } = input.switches;
  return {
    kind: 'reliefHeights', id, mapsKey, maps: first ? input.maps : null,
    switches: { reliefInert, reliefUnconditioned, reliefRepeat },
    bounds: input.bounds, radiusM: input.radiusM, quads: input.quads,
  };
}

// -----------------------------------------------------------------------------
// The client
// -----------------------------------------------------------------------------

export interface TileWorkerHandle {
  /** Decode a height field. `raw` is TRANSFERRED, so do not read it after. */
  decodeField(input: DecodeFieldInput): Promise<FieldBuffers>;
  /**
   * Decode one quadtree tile. `height` and `albedo` are TRANSFERRED, so do not
   * read them after. An albedo that comes back in `albedoRaw` is one this
   * thread could not turn into a bitmap; finish it on the main thread.
   */
  decodeTile(input: DecodeTileInput): Promise<DecodedTilePayloads>;
  /** Build a plot's surface lattice. Nothing is transferred in. */
  buildLattice(input: BuildLatticeInput): Promise<LatticeBuffers>;
  /** Evaluate one tile's relief lattice. The maps are copied to a worker the first time it sees them. */
  reliefHeights(input: ReliefHeightsInput): Promise<ReliefHeights>;
  /** False once dispose() has terminated the worker. */
  readonly live: boolean;
  /** Terminates the worker and rejects everything still in flight. */
  dispose(): void;
}

interface Pending {
  /** Which response kind this id is waiting for; anything else is a fault. */
  readonly kind: TileWorkerRequest['kind'];
  resolve(res: TileWorkerResponse): void;
  reject(err: Error): void;
}

/** How many worker threads this module has open. Read by field-agreement.mjs. */
let liveWorkers = 0;

export function liveTileWorkerCount(): number {
  return liveWorkers;
}

/**
 * One worker, one handle. The Atlas makes one per session on first need and
 * disposes it with the rest of the tier; nothing here is a singleton, so a
 * closed Atlas leaves no thread behind.
 */
export function createTileWorker(): TileWorkerHandle {
  const worker = new Worker(new URL('./tileWorker.ts', import.meta.url), {
    type: 'module',
    name: 'fxciv-tile',
  });
  liveWorkers += 1;
  const pending = new Map<number, Pending>();
  let nextId = 1;
  let live = true;
  let terminated = false;
  const reliefSent = new Set<number>();

  // liveWorkers counts threads this module has open, so it comes down on
  // whichever path retires this one first - an error event or dispose().
  let counted = true;
  const uncount = (): void => {
    if (!counted) return;
    counted = false;
    liveWorkers -= 1;
  };

  const failAll = (err: Error): void => {
    const waiting = [...pending.values()];
    pending.clear();
    for (const p of waiting) p.reject(err);
  };

  worker.addEventListener('message', (e: MessageEvent<TileWorkerResponse>) => {
    const res = e.data;
    const p = pending.get(res.id);
    if (!p) {
      // `messageerror` fails everything in flight, so a real response for one
      // of those ids arrives with nobody waiting - and its bitmap was created
      // by the worker, which means nothing else will ever close it.
      if (res.kind === 'decodeTile') {
        res.albedo?.source.close();
        res.artificial?.source.close();
      }
      return;
    }
    pending.delete(res.id);
    if (res.kind === 'error') p.reject(new Error(res.message));
    else if (res.kind !== p.kind) {
      p.reject(new Error(`tile worker answered a ${p.kind} with a ${res.kind}`));
    } else p.resolve(res);
  });
  // The worker reports a failed DECODE as an error response, so an error EVENT
  // is the module failing to load or the thread going away - not recoverable,
  // and the handle stops accepting work rather than queueing into a void.
  worker.addEventListener('error', (e: ErrorEvent) => {
    live = false;
    uncount();
    failAll(new Error(`tile worker failed: ${e.message || 'no message'}`));
  });
  // A response that could not be deserialised arrives with no id, so which
  // request it belonged to is unknowable and everything in flight has to fail
  // rather than wait for a message that already came and went. The thread
  // itself is unharmed, so the handle stays live.
  worker.addEventListener('messageerror', () => {
    failAll(new Error('tile worker sent a response that could not be deserialised'));
  });

  return {
    get live(): boolean {
      return live;
    },
    decodeField(input: DecodeFieldInput): Promise<FieldBuffers> {
      if (!live) return Promise.reject(new Error('tile worker is disposed'));
      const id = nextId++;
      const req: DecodeFieldRequest = { kind: 'decodeField', id, ...input };
      return new Promise<FieldBuffers>((resolve, reject) => {
        pending.set(id, {
          kind: 'decodeField',
          resolve: (res) => {
            if (res.kind !== 'decodeField') throw new Error('unreachable');
            resolve({ norm: res.norm, height: res.height, mips: [...res.mips] });
          },
          reject,
        });
        worker.postMessage(req, [input.raw]);
      });
    },
    decodeTile(input: DecodeTileInput): Promise<DecodedTilePayloads> {
      if (!live) return Promise.reject(new Error('tile worker is disposed'));
      const id = nextId++;
      const req: DecodeTileRequest = { kind: 'decodeTile', id, ...input };
      const transfer: Transferable[] = [];
      if (input.height) transfer.push(input.height);
      if (input.albedo) transfer.push(input.albedo);
      if (input.artificial) transfer.push(input.artificial);
      return new Promise<DecodedTilePayloads>((resolve, reject) => {
        pending.set(id, {
          kind: 'decodeTile',
          resolve: (res) => {
            if (res.kind !== 'decodeTile') throw new Error('unreachable');
            resolve({
              height: res.height, albedo: res.albedo, albedoRaw: res.albedoRaw, bounds: res.bounds,
              artificial: res.artificial, artificialRaw: res.artificialRaw,
              claims: res.claims,
            });
          },
          reject,
        });
        worker.postMessage(req, transfer);
      });
    },
    buildLattice(input: BuildLatticeInput): Promise<LatticeBuffers> {
      if (!live) return Promise.reject(new Error('tile worker is disposed'));
      const id = nextId++;
      const req: BuildLatticeRequest = { kind: 'buildLattice', id, ...input };
      return new Promise<LatticeBuffers>((resolve, reject) => {
        pending.set(id, {
          kind: 'buildLattice',
          resolve: (res) => {
            if (res.kind !== 'buildLattice') throw new Error('unreachable');
            resolve({ position: res.position, index: res.index });
          },
          reject,
        });
        worker.postMessage(req, []);
      });
    },
    reliefHeights(input: ReliefHeightsInput): Promise<ReliefHeights> {
      if (!live) return Promise.reject(new Error('tile worker is disposed'));
      const id = nextId++;
      const req = reliefRequest(id, input, reliefSent);
      return new Promise<ReliefHeights>((resolve, reject) => {
        pending.set(id, {
          kind: 'reliefHeights',
          resolve: (res) => {
            if (res.kind !== 'reliefHeights') throw new Error('unreachable');
            resolve({ heights: res.heights, ms: res.ms });
          },
          reject,
        });
        worker.postMessage(req, []);
      });
    },
    dispose(): void {
      if (terminated) return;
      terminated = true;
      live = false;
      uncount();
      worker.terminate();
      failAll(new Error('tile worker disposed while a decode was in flight'));
    },
  };
}

// -----------------------------------------------------------------------------
// The pool
// -----------------------------------------------------------------------------

export interface TileWorkerPoolHandle extends TileWorkerHandle {
  /** Worker threads the pool opened, fixed at creation. */
  readonly size: number;
  /**
   * Decodes dispatched to each worker so far, index-aligned with the pool and
   * never decremented. For an instrument proving dispatch actually spreads
   * across more than one worker; nothing here reads it for scheduling.
   */
  readonly workerLoad: readonly number[];
}

/**
 * One thread short of every core so the pool does not starve the main thread
 * and the renderer, capped at 4 because a decode is memory-bound past that at
 * the tile sizes this bakes. `navigator` is absent under Node, where a check
 * passes `size` itself.
 */
function defaultPoolSize(): number {
  const cores = typeof navigator === 'undefined' ? undefined : navigator.hardwareConcurrency;
  return Math.max(1, Math.min(4, (cores ?? 2) - 1));
}

interface PoolPending extends Pending {
  readonly workerIndex: number;
}

interface WorkerSlot {
  worker: Worker;
  live: boolean;
  inFlight: number;
  dispatched: number;
  counted: boolean;
  /** Relief map keys this worker has been sent; a respawned worker starts empty. */
  reliefSent: Set<number>;
}

/** Relief goes to the first two slots only: each holds its own 16 MB copy of the maps. */
const RELIEF_SLOTS = 2;

/**
 * `size` workers behind one `TileWorkerHandle`. Each request goes to whichever
 * worker has the fewest replies outstanding, so one slow decode does not queue
 * behind another thread that sits idle; a reply is routed back to its slot by
 * the request id the single-worker protocol already carries, in one pool-wide
 * pending map. A worker that errors is replaced in place, one at a time - its
 * own pending requests are rejected rather than resent, since their buffers
 * were already transferred away and cannot be resent, the same way the single
 * worker's pending requests failed on its own error - and every other worker's
 * work is untouched. `dispose` terminates every slot.
 */
export function createTileWorkerPool(size: number = defaultPoolSize()): TileWorkerPoolHandle {
  const poolSize = Math.max(1, size);
  const slots: WorkerSlot[] = [];
  const pending = new Map<number, PoolPending>();
  let nextId = 1;
  let terminated = false;

  const slotAt = (i: number): WorkerSlot => {
    const s = slots[i];
    if (!s) throw new Error(`tile worker pool: no slot ${i}`);
    return s;
  };

  const uncount = (i: number): void => {
    const s = slotAt(i);
    if (!s.counted) return;
    s.counted = false;
    liveWorkers -= 1;
  };

  const failSlotPending = (i: number, err: Error): void => {
    for (const [id, p] of [...pending]) {
      if (p.workerIndex !== i) continue;
      pending.delete(id);
      p.reject(err);
    }
    slotAt(i).inFlight = 0;
  };

  const spawn = (i: number): void => {
    const worker = new Worker(new URL('./tileWorker.ts', import.meta.url), {
      type: 'module',
      name: `fxciv-tile-${i}`,
    });
    slots[i] = { worker, live: true, inFlight: 0, dispatched: 0, counted: true, reliefSent: new Set() };
    liveWorkers += 1;

    worker.addEventListener('message', (e: MessageEvent<TileWorkerResponse>) => {
      const res = e.data;
      const p = pending.get(res.id);
      if (!p) {
        // Same orphaned-bitmap case as the single worker: `messageerror` or a
        // dead-slot replacement already failed this id, so nobody is waiting.
        if (res.kind === 'decodeTile') {
          res.albedo?.source.close();
          res.artificial?.source.close();
        }
        return;
      }
      pending.delete(res.id);
      const s = slotAt(p.workerIndex);
      s.inFlight = Math.max(0, s.inFlight - 1);
      if (res.kind === 'error') p.reject(new Error(res.message));
      else if (res.kind !== p.kind) {
        p.reject(new Error(`tile worker answered a ${p.kind} with a ${res.kind}`));
      } else p.resolve(res);
    });
    worker.addEventListener('error', (e: ErrorEvent) => {
      if (terminated) return;
      slotAt(i).live = false;
      uncount(i);
      failSlotPending(i, new Error(`tile worker failed: ${e.message || 'no message'}`));
      spawn(i);
    });
    worker.addEventListener('messageerror', () => {
      failSlotPending(i, new Error('tile worker sent a response that could not be deserialised'));
    });
  };
  for (let i = 0; i < poolSize; i++) spawn(i);

  const leastLoaded = (among: number = poolSize): number => {
    let best = 0;
    for (let i = 1; i < Math.min(among, poolSize); i++) {
      if (slotAt(i).inFlight < slotAt(best).inFlight) best = i;
    }
    return best;
  };

  const send = (i: number, req: TileWorkerRequest, transfer: Transferable[], reject: (reason?: unknown) => void): void => {
    const s = slotAt(i);
    s.inFlight += 1;
    s.dispatched += 1;
    try {
      s.worker.postMessage(req, transfer);
    } catch (err) {
      pending.delete(req.id);
      s.inFlight = Math.max(0, s.inFlight - 1);
      reject(err);
    }
  };

  return {
    get live(): boolean {
      return slots.some((s) => s.live);
    },
    get size(): number {
      return poolSize;
    },
    get workerLoad(): readonly number[] {
      return slots.map((s) => s.dispatched);
    },
    decodeField(input: DecodeFieldInput): Promise<FieldBuffers> {
      if (!slots.some((s) => s.live)) return Promise.reject(new Error('tile worker is disposed'));
      const id = nextId++;
      const i = leastLoaded();
      const req: DecodeFieldRequest = { kind: 'decodeField', id, ...input };
      return new Promise<FieldBuffers>((resolve, reject) => {
        pending.set(id, {
          kind: 'decodeField',
          workerIndex: i,
          resolve: (res) => {
            if (res.kind !== 'decodeField') throw new Error('unreachable');
            resolve({ norm: res.norm, height: res.height, mips: [...res.mips] });
          },
          reject,
        });
        send(i, req, [input.raw], reject);
      });
    },
    decodeTile(input: DecodeTileInput): Promise<DecodedTilePayloads> {
      if (!slots.some((s) => s.live)) return Promise.reject(new Error('tile worker is disposed'));
      const id = nextId++;
      const i = leastLoaded();
      const req: DecodeTileRequest = { kind: 'decodeTile', id, ...input };
      const transfer: Transferable[] = [];
      if (input.height) transfer.push(input.height);
      if (input.albedo) transfer.push(input.albedo);
      if (input.artificial) transfer.push(input.artificial);
      return new Promise<DecodedTilePayloads>((resolve, reject) => {
        pending.set(id, {
          kind: 'decodeTile',
          workerIndex: i,
          resolve: (res) => {
            if (res.kind !== 'decodeTile') throw new Error('unreachable');
            resolve({
              height: res.height, albedo: res.albedo, albedoRaw: res.albedoRaw, bounds: res.bounds,
              artificial: res.artificial, artificialRaw: res.artificialRaw,
              claims: res.claims,
            });
          },
          reject,
        });
        send(i, req, transfer, reject);
      });
    },
    buildLattice(input: BuildLatticeInput): Promise<LatticeBuffers> {
      if (!slots.some((s) => s.live)) return Promise.reject(new Error('tile worker is disposed'));
      const id = nextId++;
      const i = leastLoaded();
      const req: BuildLatticeRequest = { kind: 'buildLattice', id, ...input };
      return new Promise<LatticeBuffers>((resolve, reject) => {
        pending.set(id, {
          kind: 'buildLattice',
          workerIndex: i,
          resolve: (res) => {
            if (res.kind !== 'buildLattice') throw new Error('unreachable');
            resolve({ position: res.position, index: res.index });
          },
          reject,
        });
        send(i, req, [], reject);
      });
    },
    reliefHeights(input: ReliefHeightsInput): Promise<ReliefHeights> {
      if (!slots.some((s) => s.live)) return Promise.reject(new Error('tile worker is disposed'));
      const id = nextId++;
      const i = leastLoaded(RELIEF_SLOTS);
      const req = reliefRequest(id, input, slotAt(i).reliefSent);
      return new Promise<ReliefHeights>((resolve, reject) => {
        pending.set(id, {
          kind: 'reliefHeights',
          workerIndex: i,
          resolve: (res) => {
            if (res.kind !== 'reliefHeights') throw new Error('unreachable');
            resolve({ heights: res.heights, ms: res.ms });
          },
          reject,
        });
        send(i, req, [], reject);
      });
    },
    dispose(): void {
      if (terminated) return;
      terminated = true;
      for (let i = 0; i < poolSize; i++) {
        const s = slotAt(i);
        if (!s.live) continue;
        s.live = false;
        uncount(i);
        s.worker.terminate();
      }
      const waiting = [...pending.values()];
      pending.clear();
      for (const p of waiting) {
        p.reject(new Error('tile worker disposed while a decode was in flight'));
      }
    },
  };
}

// -----------------------------------------------------------------------------
// The worker side
// -----------------------------------------------------------------------------

interface WorkerScope {
  addEventListener(type: 'message', h: (e: MessageEvent<TileWorkerRequest>) => void): void;
  postMessage(res: TileWorkerResponse, transfer: Transferable[]): void;
}

/** This worker's relief maps by key; two at most, the current body's and one before it. */
const heldReliefMaps = new Map<number, TileReliefMaps>();

async function decode(
  req: TileWorkerRequest,
): Promise<{ res: TileWorkerResponse; transfer: Transferable[] }> {
  switch (req.kind) {
    case 'reliefHeights': {
      if (req.maps !== null) {
        heldReliefMaps.set(req.mapsKey, req.maps);
        for (const k of heldReliefMaps.keys()) {
          if (heldReliefMaps.size <= 2) break;
          heldReliefMaps.delete(k);
        }
      }
      const maps = heldReliefMaps.get(req.mapsKey);
      if (!maps) throw new Error(`relief maps ${req.mapsKey} were never sent to this worker`);
      const t0 = performance.now();
      const heights = tileReliefHeights(maps, req.switches, tileReliefLattice(req.bounds, req.radiusM, req.quads));
      return {
        res: { kind: 'reliefHeights', id: req.id, heights, ms: performance.now() - t0 },
        transfer: [heights.buffer],
      };
    }
    case 'decodeField': {
      const b: FieldBuffers = buildFieldBuffers(
        new Uint16Array(req.raw), req.width, req.height, req.minM, req.reliefM,
      );
      return {
        res: { kind: 'decodeField', id: req.id, norm: b.norm, height: b.height, mips: b.mips },
        transfer: [b.norm.buffer, b.height.buffer, ...b.mips.map((m) => m.data.buffer)],
      };
    }
    case 'buildLattice': {
      const b: LatticeBuffers = buildLatticeBuffers(
        req.radiusM, req.rings, req.places, req.origin,
      );
      return {
        res: { kind: 'buildLattice', id: req.id, position: b.position, index: b.index },
        transfer: [b.position.buffer, b.index.buffer],
      };
    }
    case 'decodeTile': {
      const p: DecodedTilePayloads = await decodeTilePayloads(
        req.key, req.radiusM, req.height, req.albedo,
        req.claimRes !== null && req.claimCells !== null
          ? { res: req.claimRes, cells: req.claimCells } : null,
        req.artificial,
      );
      const transfer: Transferable[] = [];
      if (p.height) transfer.push(p.height.buffer);
      if (p.albedo) transfer.push(p.albedo.source);
      if (p.albedoRaw) transfer.push(p.albedoRaw);
      if (p.artificial) transfer.push(p.artificial.source);
      if (p.artificialRaw) transfer.push(p.artificialRaw);
      if (p.claims) transfer.push(p.claims.data.buffer, p.claims.cells.buffer);
      return {
        res: {
          kind: 'decodeTile',
          id: req.id,
          height: p.height,
          albedo: p.albedo,
          albedoRaw: p.albedoRaw,
          artificial: p.artificial,
          artificialRaw: p.artificialRaw,
          bounds: p.bounds,
          claims: p.claims,
        },
        transfer,
      };
    }
  }
}

// Only a worker's global scope carries `WorkerGlobalScope`; a page and plain Node
// do not, so the client lives in this module without installing a listener there.
if ('WorkerGlobalScope' in globalThis) {
  const scope = globalThis as unknown as WorkerScope;
  scope.addEventListener('message', (e: MessageEvent<TileWorkerRequest>) => {
    const req = e.data;
    void (async () => {
      try {
        const { res, transfer } = await decode(req);
        scope.postMessage(res, transfer);
      } catch (err) {
        scope.postMessage(
          { kind: 'error', id: req.id, message: err instanceof Error ? err.message : String(err) },
          [],
        );
      }
    })();
  });
}
